import { useSelector } from 'react-redux';
import { DefaultRootState } from '../persistState';
import { createSelector } from '@reduxjs/toolkit';


export const selectUser = () => {
	const user = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
  return user
};

export interface GoogleAuthType {
	accessToken: string;
	refreshToken: string;
	expiry_date: number;
	googleId: string;
  email: string;
  scopes: string[];
}

export const userAuths = () => {
	const userAuth = useSelector(
		(state: DefaultRootState) =>
			state.promiseData.data.userData.authentications
	);
	return userAuth
};

export const googleAuths = () => {
	const googleAuth = useSelector(
		(state: DefaultRootState) =>
			state.promiseData.data.userData.authentications?.google
	);
	return googleAuth;
};

// Memoized selectors to prevent unnecessary re-renders
// Base selectors
const selectUserData = (state: DefaultRootState) => state.promiseData.data.userData;

// Specific selectors that don't include favourites
export const selectUserId = createSelector(
  [selectUserData],
  (userData) => userData?.id
);

export const selectUserProfile = createSelector(
  [selectUserData],
  (userData) => ({
    id: userData?.id,
    email: userData?.email,
    profile_information: userData?.profile_information,
    avatar: userData?.avatar,
  })
);

export const selectUserAuthentications = createSelector(
  [selectUserData],
  (userData) => userData?.authentications
);

// Separate selector for favourites to isolate re-renders
export const selectFavourites = createSelector(
  [selectUserData],
  (userData) => userData?.favourites || { projects: [], data: [], news: [] }
);

export const selectFavouriteData = createSelector(
  [selectFavourites],
  (favourites) => favourites?.data || []
);

export const selectFavouriteProjects = createSelector(
  [selectFavourites],
  (favourites) => favourites?.projects || []
);

export const selectFavouriteNews = createSelector(
  [selectFavourites],
  (favourites) => favourites?.news || []
);

// Selector factory for checking if a specific item is favourited
export const makeSelectIsFavourite = (itemId: string, type: 'data' | 'projects' | 'news') =>
  createSelector(
    [selectFavourites],
    (favourites) => favourites?.[type]?.includes(itemId) || false
  );

// Additional specific selectors for common use cases
export const selectUserCompanies = createSelector(
  [selectUserData],
  (userData) => userData?.companies || []
);

export const selectUserBilling = createSelector(
  [selectUserData],
  (userData) => userData?.billing || {}
);

export const selectUserAvatar = createSelector(
  [selectUserData],
  (userData) => userData?.avatar || userData?.profile_information?.avatar
);

export const selectUserEmail = createSelector(
  [selectUserData],
  (userData) => userData?.email
);

export const selectUserName = createSelector(
  [selectUserData],
  (userData) => ({
    name: userData?.profile_information?.name || userData?.name,
    surname: userData?.profile_information?.surname || userData?.surname,
    fullName: `${userData?.profile_information?.name || userData?.name || ''} ${userData?.profile_information?.surname || userData?.surname || ''}`.trim()
  })
);

export const selectUserPosts = createSelector(
  [selectUserData],
  (userData) => userData?.posts || { liked_posts: [] }
);

export const selectUserAchievements = createSelector(
  [selectUserData],
  (userData) => userData?.achievements || {}
);

export const selectUserTutorials = createSelector(
  [selectUserData],
  (userData) => userData?.tutorials || {}
);

// Context-specific selector to prevent unnecessary re-renders
export const selectUserContext = createSelector(
  [selectUserData],
  (userData) => userData?.profile_information?.context || {}
);

export const selectUserContextWeights = createSelector(
  [selectUserContext],
  (context) => context?.weights || {}
);

const userSelect = createSelector(selectUser);